import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Shield, AlertTriangle, CheckCircle, Clock, Globe, Loader2, Zap, Target, Download, Lock, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

const DPE_CONFIG = {
  A: { color: '#22c55e', label: 'Excellent'   },
  B: { color: '#84cc16', label: 'Très bon'    },
  C: { color: '#eab308', label: 'Bon'         },
  D: { color: '#f97316', label: 'Moyen'       },
  E: { color: '#ef4444', label: 'Insuffisant' },
  F: { color: '#dc2626', label: 'Faible'      },
  G: { color: '#991b1b', label: 'Critique'    },
};

const getRiskFromScore = (score) => {
  if (score >= 80) return 'EXCELLENT';
  if (score >= 60) return 'MOYEN';
  if (score >= 40) return 'ÉLEVÉ';
  return 'CRITIQUE';
};

// CORRECTION : la note A-G est calculée à partir du score
const getGradeFromScore = (score) => {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  if (score >= 45) return 'E';
  if (score >= 30) return 'F';
  return 'G';
};

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedCorrections, setExpandedCorrections] = useState({});
  const reportRef = useRef(null);

  const plan = user?.subscription_plan || 'free';
  const canExport = plan === 'enterprise';

  useEffect(() => {
    if (!id || !token) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/reports/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReport(res.data);
      } catch (err) {
        setError("Rapport introuvable.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, token]);

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: '#09090b', useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = pdf.internal.pageSize.getHeight();
      let heightLeft = pdfHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`cyberguard-rapport-${report.target_domain}-${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`);
    } catch (err) {
      console.error('Erreur export PDF:', err);
    } finally {
      setExporting(false);
    }
  };

  const riskColors = {
    'EXCELLENT': { text: 'text-green-500',  bg: 'bg-green-500/10',  border: 'border-green-500/30'  },
    'MOYEN':     { text: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    'ÉLEVÉ':     { text: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
    'CRITIQUE':  { text: 'text-red-500',    bg: 'bg-red-500/10',    border: 'border-red-500/30'    },
  };

  const severityColor = (s) => s === 'high' ? 'text-red-500 border-red-500/20' : s === 'medium' ? 'text-yellow-500 border-yellow-500/20' : 'text-blue-400 border-blue-400/20';
  const severityLabel = (s) => s === 'high' ? 'Critique' : s === 'medium' ? 'Moyen' : 'Faible';
  const timelineColor = (t) => t === 'Immédiat' ? 'text-red-400' : t === 'Court terme' ? 'text-yellow-400' : 'text-blue-400';

  if (loading) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        <p className="font-mono text-xs text-zinc-500 uppercase tracking-widest animate-pulse">Chargement du rapport...</p>
      </div>
    </DashboardLayout>
  );

  if (error || !report) return (
    <DashboardLayout>
      <div className="max-w-md mx-auto mt-20 p-8 border border-red-900/30 bg-red-500/5 text-center space-y-4">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto" />
        <p className="text-zinc-400 font-mono text-sm">{error}</p>
        <button onClick={() => navigate('/reports')} className="text-blue-500 font-mono text-xs border border-blue-500/30 px-4 py-2">Retour aux rapports</button>
      </div>
    </DashboardLayout>
  );

  const riskLevel = getRiskFromScore(report.security_score);
  const risk = riskColors[riskLevel];
  // CORRECTION : plus de "D" par défaut, la note suit toujours le score
  const dpeGrade = getGradeFromScore(report.security_score);
  const dpe = DPE_CONFIG[dpeGrade];

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-8 space-y-6 animate-in fade-in duration-500">

        {/* Nav + Export */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-[10px] font-mono uppercase tracking-widest">
            <ArrowLeft className="w-3 h-3" /> Retour_aux_rapports
          </button>
          {canExport ? (
            <button onClick={handleExportPDF} disabled={exporting} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-mono text-xs uppercase rounded-sm transition-all">
              {exporting ? <><Loader2 className="w-3 h-3 animate-spin" /> Export...</> : <><Download className="w-3 h-3" /> Exporter_PDF</>}
            </button>
          ) : (
            <button onClick={() => navigate('/billing')} className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-500 hover:text-white hover:border-green-500/50 font-mono text-[10px] uppercase transition-all rounded-sm">
              <Lock className="w-3 h-3" /> Export PDF — Plan Enterprise
            </button>
          )}
        </div>

        <div ref={reportRef} className="space-y-6">

          {/* Header */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <div className="p-8 bg-black/40 flex flex-col md:flex-row justify-between gap-6">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-2 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-500 text-[9px] font-mono rounded-sm uppercase">
                  <Shield className="w-3 h-3" /> AI_Security_Report
                </div>
                <h1 className="text-3xl font-black text-white uppercase tracking-tighter font-mono">
                  {report.audit_name || report.target_domain}
                </h1>
                <div className="flex flex-wrap gap-5 text-zinc-500 text-[10px] font-mono uppercase">
                  <span className="flex items-center gap-1"><Globe className="w-3 h-3 text-blue-500" /> {report.target_domain}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(report.created_at).toLocaleString('fr-FR')}</span>
                </div>
              </div>

              {/* Score + DPE */}
              <div className="flex items-center gap-6 shrink-0">
                <div className={`flex flex-col items-center justify-center px-6 py-4 rounded-sm border ${risk.bg} ${risk.border}`}>
                  <div className={`text-2xl font-black font-mono ${risk.text}`}>{riskLevel}</div>
                  <div className="text-[9px] text-zinc-500 font-mono uppercase mt-1">Niveau de risque</div>
                  <div className="text-3xl font-black text-white font-mono mt-2">{report.security_score}%</div>
                  <div className="text-[9px] text-zinc-500 font-mono uppercase">Score sécurité</div>
                </div>

                {/* Grade DPE */}
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-sm border-2 mb-1" style={{ borderColor: dpe.color, backgroundColor: dpe.color + '20' }}>
                    <span className="text-3xl font-black font-mono" style={{ color: dpe.color }}>{dpeGrade}</span>
                  </div>
                  <div className="text-[9px] text-zinc-500 font-mono uppercase">{dpe.label}</div>
                </div>
              </div>
            </div>

            {/* Barre DPE */}
            <div className="px-8 py-3 bg-black/20 border-t border-zinc-800">
              <div className="flex items-center gap-0.5">
                {Object.entries(DPE_CONFIG).map(([grade, config]) => (
                  <div key={grade} className={`flex-1 h-5 flex items-center justify-center font-mono font-black text-[10px] text-white transition-all ${dpeGrade === grade ? 'scale-110' : 'opacity-30'}`} style={{ backgroundColor: config.color }}>
                    {grade}
                  </div>
                ))}
              </div>
            </div>

            <div className="px-8 py-5 border-t border-zinc-800 bg-black/20">
              <p className="text-zinc-400 text-sm font-mono leading-relaxed">{report.summary}</p>
            </div>
          </div>

          {/* Résumé RGPD */}
          {report.rgpd_summary && (
            <div className="bg-purple-500/5 border border-purple-500/20 rounded-sm p-5">
              <h2 className="text-purple-400 text-xs font-mono font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                <FileText className="w-3 h-3" /> Conformité RGPD
              </h2>
              <p className="text-zinc-300 text-sm font-mono leading-relaxed">{report.rgpd_summary}</p>
            </div>
          )}

          {/* Commentaire risque */}
          <div className={`border rounded-sm p-6 ${risk.bg} ${risk.border}`}>
            <h2 className={`text-xs font-mono font-bold uppercase tracking-widest mb-3 flex items-center gap-2 ${risk.text}`}>
              <AlertTriangle className="w-3 h-3" /> Analyse_du_Niveau_de_Risque
            </h2>
            <p className="text-zinc-300 text-sm font-mono leading-relaxed">{report.risk_comment}</p>
          </div>

          {/* Vulnérabilités avec guide de correction */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-4">
            <h2 className="text-white text-xs font-mono font-bold uppercase tracking-widest border-l-2 border-red-500 pl-4 flex items-center gap-2">
              <AlertTriangle className="w-3 h-3 text-red-500" /> Vulnérabilités_Détaillées
            </h2>
            {report.vulnerabilities_explained?.map((v, i) => (
              <div key={i} className={`border rounded-sm overflow-hidden ${severityColor(v.severity)}`}>
                <div className="p-4 bg-black/40">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-sm font-mono font-bold">{v.title}</span>
                    <span className={`text-[9px] font-mono uppercase px-2 py-0.5 border rounded-sm ${severityColor(v.severity)}`}>
                      {severityLabel(v.severity)}
                    </span>
                  </div>
                  <p className="text-zinc-400 text-xs font-mono leading-relaxed">{v.description}</p>
                  {v.simple_fix && (
                    <p className="text-blue-400 text-[10px] font-mono mt-2 border-t border-zinc-800 pt-2">
                      💬 À dire à votre développeur : {v.simple_fix}
                    </p>
                  )}
                </div>

                {/* Guide de correction accordéon */}
                {v.correction_guide && (
                  <div>
                    <button
                      onClick={() => setExpandedCorrections(prev => ({ ...prev, [i]: !prev[i] }))}
                      className="w-full flex items-center justify-between px-4 py-2 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                    >
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                        🔧 Guide de correction détaillé
                      </span>
                      {expandedCorrections[i] ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
                    </button>
                    {expandedCorrections[i] && (
                      <div className="px-4 py-3 bg-zinc-900/80 border-t border-zinc-800">
                        <p className="text-zinc-300 text-xs font-mono leading-relaxed whitespace-pre-line">{v.correction_guide}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Recommandations */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-4">
            <h2 className="text-white text-xs font-mono font-bold uppercase tracking-widest border-l-2 border-blue-500 pl-4 flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-blue-500" /> Recommandations_Concrètes
            </h2>
            {report.recommendations?.map((r, i) => (
              <div key={i} className="p-4 bg-black/40 border border-zinc-800 hover:border-blue-500/30 transition-all rounded-sm">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500/20 border border-blue-500/30 flex items-center justify-center rounded-sm shrink-0 mt-0.5">
                    <span className="text-blue-400 text-[10px] font-black font-mono">{r.priority}</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-white text-sm font-mono font-bold">{r.action}</p>
                    <p className="text-zinc-400 text-xs font-mono leading-relaxed">{r.detail}</p>
                    <p className="text-blue-400 text-[10px] font-mono uppercase mt-2">→ Impact : {r.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Plan d'action */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-3">
            <h2 className="text-white text-xs font-mono font-bold uppercase tracking-widest border-l-2 border-green-500 pl-4 flex items-center gap-2">
              <Target className="w-3 h-3 text-green-500" /> Plan_d'Action_Priorisé
            </h2>
            {report.action_plan?.map((step, i) => (
              <div key={i} className="flex items-center gap-4 p-4 bg-black/40 border border-zinc-800 rounded-sm">
                <div className="w-8 h-8 bg-green-500/10 border border-green-500/20 flex items-center justify-center rounded-sm shrink-0">
                  <Zap className="w-3 h-3 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-white text-xs font-mono font-bold">{step.task}</p>
                </div>
                <span className={`text-[9px] font-mono uppercase font-bold shrink-0 ${timelineColor(step.timeline)}`}>
                  {step.timeline}
                </span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-zinc-800 pt-4 flex items-center justify-between">
            <span className="text-zinc-600 font-mono text-[9px] uppercase">CyberGuard — contact@cyber-guard.fr</span>
            {/* CORRECTION : date du rapport au lieu de la date du jour */}
            <span className="text-zinc-600 font-mono text-[9px] uppercase">Généré le {new Date(report.created_at).toLocaleDateString('fr-FR')}</span>
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
}
